import { useState } from 'react';
import { useClub } from '../app/club';
import { useRepo } from '../app/services';
import { useClubMutation } from '../data/hooks';
import type { Group, Person } from '../lib/types';
import { resolveExpiry, UNSET_EXPIRY, type ExpiryChoice } from '../lib/expiry';
import { Button, Dialog, Field, useToast } from './ui';
import { ExpiryChoiceField } from './ExpiryChoiceField';
import { errorText } from '../lib/errors';

/**
 * Add a person to a group. Either the group or the person is fixed; the other is picked.
 * The end date must be chosen explicitly.
 */
export function MembershipDialog({
  open,
  onOpenChange,
  groups,
  persons,
  fixedGroup,
  fixedPerson,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  groups: Group[];
  persons: Person[];
  fixedGroup?: Group;
  fixedPerson?: Person;
}) {
  const { orgId } = useClub();
  const repo = useRepo();
  const toast = useToast();
  const [groupId, setGroupId] = useState(fixedGroup?.id ?? '');
  const [personId, setPersonId] = useState(fixedPerson?.id ?? '');
  const [expiry, setExpiry] = useState<ExpiryChoice>(UNSET_EXPIRY);
  const [errors, setErrors] = useState<{ group?: string; person?: string; expiry?: string; form?: string }>({});
  const add = useClubMutation(orgId, (v: { group_id: string; person_id: string; valid_until: string | null }) => repo.addGroupMember(v));
  const group = groups.find((g) => g.id === (fixedGroup?.id ?? groupId));

  const reset = () => {
    setGroupId(fixedGroup?.id ?? '');
    setPersonId(fixedPerson?.id ?? '');
    setExpiry(UNSET_EXPIRY);
    setErrors({});
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: typeof errors = {};
    const g = fixedGroup?.id ?? groupId;
    const p = fixedPerson?.id ?? personId;
    if (!g) errs.group = 'Kies een groep.';
    if (!p) errs.person = 'Kies een persoon.';
    const res = resolveExpiry(expiry, { groupValidUntil: group?.valid_until });
    if (!res.ok) errs.expiry = res.error;
    setErrors(errs);
    if (Object.keys(errs).length || !res.ok) return;
    try {
      await add.mutateAsync({ group_id: g, person_id: p, valid_until: res.validUntil });
      toast('Toegevoegd. Opgeslagen; gaat bij de volgende sync naar het slot.');
      reset();
      onOpenChange(false);
    } catch (err) {
      setErrors({ form: errorText(err, 'Opslaan mislukt') });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
      title={fixedGroup ? `Lid toevoegen aan ${fixedGroup.name}` : `${fixedPerson?.full_name} toevoegen aan groep`}
      description="De persoon krijgt toegang tot de deuren en tijden van de groep."
    >
      <form className="stack" onSubmit={submit} noValidate>
        {!fixedGroup && (
          <Field label="Groep" error={errors.group}>
            {(p) => (
              <select {...p} className="input" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                <option value="">Kies een groep</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
        {!fixedPerson && (
          <Field label="Persoon" error={errors.person}>
            {(p) => (
              <select {...p} className="input" value={personId} onChange={(e) => setPersonId(e.target.value)}>
                <option value="">Kies een persoon</option>
                {persons.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.full_name}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
        <ExpiryChoiceField value={expiry} onChange={setExpiry} error={errors.expiry} groupValidUntil={group?.valid_until} />
        {errors.form && (
          <div className="alert alert-danger" role="alert">
            {errors.form}
          </div>
        )}
        <div className="dialog-actions">
          <Button onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button variant="primary" type="submit" disabled={add.isPending}>
            Toevoegen
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
